import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";

import {
  CARD,
  CARD_SHADOW,
  DEEP,
  FAINT,
  GOAL_PCT,
  GRID,
  HAIR,
  INK,
  MUTED,
  NEG,
  ORANGE,
  POS,
  SKY,
  TEAL,
  VOLUME_GOAL,
  fmtInt,
  fmtPct,
  pct,
} from "./retellCallsTheme";

export function MetricCard({
  label,
  value,
  sub,
  accent,
}: {
  accent: string;
  label: string;
  sub?: string;
  value: string;
}) {
  return (
    <div
      style={{
        background: CARD,
        borderRadius: 12,
        padding: "11px 13px",
        boxShadow: CARD_SHADOW,
        border: `1px solid ${HAIR}`,
        borderLeft: `4px solid ${accent}`,
        flex: "1 1 150px",
        minWidth: 140,
      }}
    >
      <div
        style={{
          fontSize: "0.62rem",
          fontWeight: 700,
          color: MUTED,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: "1.5rem",
          fontWeight: 800,
          color: INK,
          marginTop: 3,
          letterSpacing: "-0.02em",
        }}
      >
        {value}
      </div>
      {sub && (
        <div
          style={{
            fontSize: "0.66rem",
            color: MUTED,
            marginTop: 3,
            lineHeight: 1.25,
          }}
        >
          {sub}
        </div>
      )}
    </div>
  );
}

export function StatCard({ label, value, sub }: { label: string; sub?: string; value: string }) {
  return (
    <div
      style={{
        background: GRID,
        border: `1px solid ${HAIR}`,
        borderRadius: 10,
        padding: "10px 11px",
      }}
    >
      <div
        style={{
          fontSize: "0.6rem",
          fontWeight: 700,
          color: MUTED,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: "1.15rem",
          fontWeight: 800,
          color: INK,
          marginTop: 4,
          letterSpacing: "-0.02em",
        }}
      >
        {value}
      </div>
      {sub && (
        <div
          style={{
            fontSize: "0.66rem",
            color: MUTED,
            marginTop: 4,
            lineHeight: 1.25,
          }}
        >
          {sub}
        </div>
      )}
    </div>
  );
}

export function Section({
  title,
  legend,
  children,
}: {
  children: ReactNode;
  legend?: ReactNode;
  title: string;
}) {
  return (
    <div
      style={{
        background: CARD,
        border: `1px solid ${HAIR}`,
        borderRadius: 14,
        boxShadow: CARD_SHADOW,
        padding: "0.9rem 1rem",
        minWidth: 0,
        overflowX: "auto",
      }}
    >
      <div
        style={{
          fontWeight: 700,
          fontSize: "0.85rem",
          color: INK,
          marginBottom: "0.6rem",
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
        }}
      >
        {title}
        {legend && <span style={{ marginLeft: "auto" }}>{legend}</span>}
      </div>
      {children}
    </div>
  );
}

export function LegendSwatch({ color, label }: { color: string; label: string }) {
  return (
    <span
      style={{
        fontSize: "0.7rem",
        color: MUTED,
        fontWeight: 600,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        marginLeft: 8,
      }}
    >
      <i
        style={{
          width: 9,
          height: 9,
          borderRadius: 2,
          display: "inline-block",
          background: color,
        }}
      />
      {label}
    </span>
  );
}

export function DeltaChip({ value }: { value: number }) {
  const good = value >= 0;
  const color = good ? POS : NEG;
  const bg = good ? "rgba(31,157,107,.11)" : "rgba(210,96,58,.11)";
  return (
    <span
      style={{
        fontSize: "0.7rem",
        fontWeight: 700,
        padding: "1px 6px",
        borderRadius: 6,
        color,
        background: bg,
      }}
    >
      {good ? "+" : ""}
      {value.toFixed(1)} pts
    </span>
  );
}

export function ProgressBar({ value, color }: { color: string; value: number }) {
  return (
    <div
      style={{
        height: 6,
        background: GRID,
        borderRadius: 3,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          height: "100%",
          borderRadius: 3,
          width: `${Math.min(100, value)}%`,
          background: color,
        }}
      />
    </div>
  );
}

export function MtdBand({
  mtd,
}: {
  mtd: { ended: number; intakeRatePct: number; intakes: number; scheduledCallsGoal: number | null };
}) {
  const rateGap = mtd.intakeRatePct - GOAL_PCT;
  const labelStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    fontSize: "0.72rem",
    color: MUTED,
    marginBottom: 7,
  };
  const valStyle = { color: INK, fontWeight: 800, fontSize: "0.8rem" };
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 26,
        background: CARD,
        border: `1px solid ${HAIR}`,
        borderRadius: 12,
        boxShadow: CARD_SHADOW,
        padding: "12px 20px",
        marginBottom: "1.25rem",
        minWidth: 0,
        overflowX: "auto",
      }}
    >
      <div style={{ flex: "1 1 180px", minWidth: 180 }}>
        <div style={labelStyle}>
          <span style={{ fontWeight: 600 }}>Month-to-date volume</span>
          <span style={valStyle}>
            {fmtInt(mtd.ended)}{" "}
            <span style={{ color: FAINT, fontWeight: 500 }}>/ {fmtInt(VOLUME_GOAL)}</span>
          </span>
        </div>
        <ProgressBar value={pct(mtd.ended, VOLUME_GOAL)} color={TEAL} />
      </div>
      <div style={{ width: 1, alignSelf: "stretch", background: HAIR }} />
      <div style={{ flex: "1 1 180px", minWidth: 180 }}>
        <div style={labelStyle}>
          <span style={{ fontWeight: 600 }}>
            MTD answered intake rate{" "}
            <span style={{ color: FAINT, fontWeight: 500 }}>vs {GOAL_PCT.toFixed(0)}% goal</span>
          </span>
          <span style={valStyle}>
            {fmtPct(mtd.intakeRatePct)} <DeltaChip value={rateGap} />
          </span>
        </div>
        <ProgressBar value={pct(mtd.intakeRatePct, GOAL_PCT)} color={ORANGE} />
      </div>
      {mtd.scheduledCallsGoal != null && (
        <>
          <div style={{ width: 1, alignSelf: "stretch", background: HAIR }} />
          <div style={{ flex: "1 1 180px", minWidth: 180 }}>
            <div style={labelStyle}>
              <span style={{ fontWeight: 600 }}>MTD scheduled calls</span>
              <span style={valStyle}>
                {fmtInt(mtd.intakes)}{" "}
                <span style={{ color: FAINT, fontWeight: 500 }}>
                  / {fmtInt(mtd.scheduledCallsGoal)}
                </span>
              </span>
            </div>
            <ProgressBar value={pct(mtd.intakes, mtd.scheduledCallsGoal)} color={SKY} />
          </div>
        </>
      )}
    </div>
  );
}

export const tableHeaderStyle: CSSProperties = {
  padding: "0.45rem 0.5rem",
  borderBottom: `1px solid ${HAIR}`,
  color: MUTED,
  fontSize: "0.64rem",
  fontWeight: 800,
  letterSpacing: "0.04em",
  textAlign: "right" as const,
  textTransform: "uppercase" as const,
  whiteSpace: "nowrap",
};

export const tableCellStyle: CSSProperties = {
  padding: "0.45rem 0.5rem",
  borderBottom: `1px solid ${GRID}`,
  color: INK,
  fontSize: "0.73rem",
  fontWeight: 650,
  textAlign: "right" as const,
  whiteSpace: "nowrap",
};

export function TableHeader({
  label,
  definition,
  align = "right",
  children,
  sortDirection,
}: {
  align?: "left" | "right";
  children?: ReactNode;
  definition: string;
  label: string;
  sortDirection?: "ascending" | "descending" | "none";
}) {
  const [open, setOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{
    above: boolean;
    left: number;
    top: number;
  } | null>(null);
  const tooltipId = useId();
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  function showTooltip() {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;

    const width = 230;
    const pagePadding = 8;
    const desiredLeft = align === "right" ? rect.right - width : rect.left;
    const left = Math.max(
      pagePadding,
      Math.min(desiredLeft, window.innerWidth - width - pagePadding),
    );
    const above = rect.bottom + 90 > window.innerHeight;

    setTooltipPosition({
      top: above ? rect.top - 7 : rect.bottom + 7,
      left,
      above,
    });
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return undefined;

    const closeTooltip = () => setOpen(false);
    window.addEventListener("resize", closeTooltip);
    window.addEventListener("scroll", closeTooltip, true);

    return () => {
      window.removeEventListener("resize", closeTooltip);
      window.removeEventListener("scroll", closeTooltip, true);
    };
  }, [open]);

  return (
    <th
      scope="col"
      aria-sort={sortDirection}
      style={{
        ...tableHeaderStyle,
        textAlign: align,
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: align === "right" ? "flex-end" : "flex-start",
          gap: 5,
        }}
      >
        {children ?? label}
        <span style={{ position: "relative", display: "inline-flex" }}>
          <button
            ref={buttonRef}
            type="button"
            aria-label={`About ${label}: ${definition}`}
            aria-describedby={open ? tooltipId : undefined}
            onMouseEnter={showTooltip}
            onMouseLeave={() => setOpen(false)}
            onFocus={showTooltip}
            onBlur={() => setOpen(false)}
            onClick={showTooltip}
            onKeyDown={(event) => {
              if (event.key === "Escape") setOpen(false);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 15,
              height: 15,
              padding: 0,
              border: `1px solid ${FAINT}`,
              borderRadius: "50%",
              background: "#fff",
              color: MUTED,
              fontSize: "0.62rem",
              fontWeight: 800,
              lineHeight: 1,
              cursor: "help",
            }}
          >
            ?
          </button>
          {open &&
            tooltipPosition &&
            createPortal(
              <span
                id={tooltipId}
                role="tooltip"
                style={{
                  position: "fixed",
                  top: tooltipPosition.top,
                  left: tooltipPosition.left,
                  transform: tooltipPosition.above ? "translateY(-100%)" : undefined,
                  zIndex: 1000,
                  width: 230,
                  boxSizing: "border-box",
                  padding: "0.55rem 0.65rem",
                  borderRadius: 7,
                  background: INK,
                  color: "#fff",
                  boxShadow: "0 8px 20px rgba(22,37,46,.2)",
                  fontSize: "0.72rem",
                  fontWeight: 500,
                  letterSpacing: "normal",
                  lineHeight: 1.4,
                  textAlign: "left",
                  textTransform: "none",
                  whiteSpace: "normal",
                }}
              >
                {definition}
              </span>,
              document.body,
            )}
        </span>
      </span>
    </th>
  );
}

export function csvEscape(value: unknown) {
  const str = value === null || value === undefined ? "" : String(value);
  return /[",\n]/.test(str) ? `"${str.replaceAll('"', '""')}"` : str;
}

export function downloadCsv(
  filename: string,
  rows: Array<Record<string, unknown>>,
  columns: Array<{ key: string; label: string }>,
) {
  const header = columns.map((column) => csvEscape(column.label)).join(",");
  const body = rows.map((row) => columns.map((column) => csvEscape(row[column.key])).join(","));
  const blob = new Blob([[header, ...body].join("\n")], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function CsvButton({
  filename,
  rows,
  columns,
}: {
  columns: Array<{ key: string; label: string }>;
  filename: string;
  rows: Array<Record<string, unknown>>;
}) {
  return (
    <button
      type="button"
      disabled={!rows.length}
      onClick={() => downloadCsv(filename, rows, columns)}
      style={{
        padding: "0.25rem 0.55rem",
        borderRadius: 6,
        fontSize: "0.68rem",
        fontWeight: 750,
        cursor: rows.length ? "pointer" : "not-allowed",
        background: rows.length ? "#eff6ff" : GRID,
        color: rows.length ? DEEP : FAINT,
        border: `1px solid ${rows.length ? "#bfdbfe" : HAIR}`,
      }}
    >
      Download CSV
    </button>
  );
}
